import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getInspections, getInspectionAnnualStatus, createInspection, archiveInspection,
} from "../../api/client";
import DocumentsPanel from "../Documents/DocumentsPanel";
import { Inspection, InspectionAnnualRow } from "../../types";

/**
 * Control Authority IU inspections (40 CFR 403.8(f)(2)(v) — inspect each SIU at
 * least once a year). Shows the annual-requirement roster (who's been inspected),
 * open re-inspection follow-ups, and the inspection log, with an add form.
 *
 * A completed inspection counts toward the annual requirement regardless of
 * outcome; a "deficiencies" result opens a follow-up that a satisfactory
 * re-inspection resolves.
 */
const TYPES = ["annual", "routine", "follow_up", "complaint", "other"];

const today = () => new Date().toISOString().slice(0, 10);

const InspectionsPanel: React.FC = () => {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [status, setStatus] = useState<{ total_ius: number; inspected: number; not_inspected: number;
    industrial_users: InspectionAnnualRow[] } | null>(null);
  const [log, setLog] = useState<Inspection[]>([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    company_id: "", inspection_date: today(), inspection_type: "annual",
    result: "satisfactory", inspector_name: "", findings: "", parent_inspection_id: "",
  });

  const load = useCallback(() => {
    getInspectionAnnualStatus(year).then(r => setStatus(r.data)).catch(() => {});
    getInspections({ year }).then(r => setLog(r.data)).catch(() => {});
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const openFollowups = useMemo(() => log.filter(i => i.follow_up_open), [log]);
  // The entry opened to show its details and its report.
  const [openId, setOpenId] = useState<number | null>(null);

  const submit = async () => {
    setError("");
    if (!form.company_id) { setError("Choose an industrial user."); return; }
    if (!form.inspection_date) { setError("Inspection date is required."); return; }
    setSaving(true);
    try {
      const created = await createInspection({
        company_id: Number(form.company_id),
        inspection_date: form.inspection_date,
        inspection_type: form.inspection_type,
        result: form.result,
        inspector_name: form.inspector_name || undefined,
        findings: form.findings || undefined,
        parent_inspection_id: form.parent_inspection_id ? Number(form.parent_inspection_id) : undefined,
      });
      setShowForm(false);
      setForm(f => ({ ...f, inspector_name: "", findings: "", result: "satisfactory",
        parent_inspection_id: "", inspection_date: today() }));
      // Open the new entry so its report can be attached straight away.
      if (created?.data?.id) setOpenId(created.data.id);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "Could not save inspection.");
    } finally {
      setSaving(false);
    }
  };

  const onArchive = async (i: Inspection) => {
    if (!window.confirm(`Archive the ${i.inspection_date} inspection of ${i.company_name}? It stays on record but is hidden.`)) return;
    try { await archiveInspection(i.id); load(); }
    catch (e: any) { setError(e?.response?.data?.error ?? "Could not archive."); }
  };

  const startReinspection = (parent: Inspection) => {
    setForm(f => ({ ...f, company_id: String(parent.company_id),
      parent_inspection_id: String(parent.id), inspection_type: "follow_up",
      result: "satisfactory", inspection_date: today() }));
    setShowForm(true);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18 }}>IU Inspections</h3>
          <p style={{ margin: "2px 0 0", color: "#718096", fontSize: 13 }}>
            The CA must inspect each significant IU at least once per calendar year (40 CFR 403.8(f)(2)(v)).
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={sel}>
            {[0, 1, 2].map(d => new Date().getFullYear() - d).map(y =>
              <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={btn} onClick={() => setShowForm(v => !v)}>
            {showForm ? "Close" : "+ Log inspection"}
          </button>
        </div>
      </div>

      {error && <p style={{ color: "#c53030", fontSize: 13 }}>{error}</p>}

      {/* Annual-requirement summary */}
      {status && (
        <div style={card}>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <Stat label="Industrial users" value={status.total_ius} />
            <Stat label="Inspected this year" value={status.inspected} color="#2f855a" />
            <Stat label="Not yet inspected" value={status.not_inspected}
                  color={status.not_inspected > 0 ? "#c53030" : "#2f855a"} />
            <Stat label="Open re-inspections" value={openFollowups.length}
                  color={openFollowups.length > 0 ? "#c05621" : "#2f855a"} />
          </div>
          {status.not_inspected > 0 && (
            <p style={{ margin: "10px 0 0", fontSize: 13, color: "#c53030" }}>
              {status.industrial_users.filter(r => !r.inspected_this_year).map(r => r.name).join(", ")}
              {" "}— not yet inspected in {year}.
            </p>
          )}
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div style={card}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={field}><span style={lbl}>Industrial user</span>
              <select style={inp} value={form.company_id}
                onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))}>
                <option value="">— select —</option>
                {(status?.industrial_users ?? []).map(r =>
                  <option key={r.company_id} value={r.company_id}>{r.name}</option>)}
              </select>
            </label>
            <label style={field}><span style={lbl}>Date</span>
              <input type="date" style={inp} value={form.inspection_date}
                onChange={e => setForm(f => ({ ...f, inspection_date: e.target.value }))} />
            </label>
            <label style={field}><span style={lbl}>Type</span>
              <select style={inp} value={form.inspection_type}
                onChange={e => setForm(f => ({ ...f, inspection_type: e.target.value }))}>
                {TYPES.map(t => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </select>
            </label>
            <label style={field}><span style={lbl}>Result</span>
              <select style={inp} value={form.result}
                onChange={e => setForm(f => ({ ...f, result: e.target.value }))}>
                <option value="satisfactory">Satisfactory</option>
                <option value="deficiencies">Deficiencies found</option>
              </select>
            </label>
            <label style={field}><span style={lbl}>Inspector</span>
              <input style={inp} value={form.inspector_name}
                onChange={e => setForm(f => ({ ...f, inspector_name: e.target.value }))} />
            </label>
            <label style={{ ...field, gridColumn: "1 / -1" }}><span style={lbl}>Findings</span>
              <textarea style={{ ...inp, minHeight: 56 }} value={form.findings}
                onChange={e => setForm(f => ({ ...f, findings: e.target.value }))} />
            </label>
          </div>
          {form.parent_inspection_id &&
            <p style={{ fontSize: 12, color: "#c05621", margin: "8px 0 0" }}>
              Logging a re-inspection of #{form.parent_inspection_id} — a satisfactory result clears its follow-up.
            </p>}
          {form.result === "deficiencies" &&
            <p style={{ fontSize: 12, color: "#c05621", margin: "8px 0 0" }}>
              Deficiencies will open a re-inspection follow-up (still counts as inspected this year).
            </p>}
          <div style={{ marginTop: 12 }}>
            <button style={btn} onClick={submit} disabled={saving}>
              {saving ? "Saving…" : "Save inspection"}
            </button>
          </div>
        </div>
      )}

      {/* Open follow-ups */}
      {openFollowups.length > 0 && (
        <div style={{ ...card, borderColor: "#f6ad55" }}>
          <strong style={{ fontSize: 14 }}>Open re-inspections ({openFollowups.length})</strong>
          {openFollowups.map(i => (
            <div key={i.id} style={row}>
              <span><strong>{i.company_name}</strong>
                <span style={meta}> · {i.inspection_date} · deficiencies</span></span>
              <button style={outline} onClick={() => startReinspection(i)}>Log re-inspection</button>
            </div>
          ))}
        </div>
      )}

      {/* Log */}
      <div style={card}>
        <strong style={{ fontSize: 14 }}>Inspection log — {year}</strong>
        {log.length === 0
          ? <p style={{ color: "#718096", fontSize: 13, margin: "6px 0 0" }}>No inspections recorded for {year}.</p>
          : log.map(i => (
            <React.Fragment key={i.id}>
            <div style={row}>
              <button type="button" style={rowBtn} aria-expanded={openId === i.id}
                title="Show the details and report" onClick={() => setOpenId(openId === i.id ? null : i.id)}>
                <span style={meta}>{openId === i.id ? "▾ " : "▸ "}</span>
                <strong>{i.company_name}</strong>
                <span style={meta}> · {i.inspection_date} · {i.inspection_type.replace("_", " ")} · </span>
                <span style={{ color: i.result === "deficiencies" ? "#c53030" : "#2f855a", fontWeight: 600 }}>
                  {i.result === "deficiencies" ? "Deficiencies" : "Satisfactory"}
                </span>
                {i.follow_up_open && <span style={{ color: "#c05621" }}> · follow-up open</span>}
                {i.parent_inspection_id && <span style={meta}> · re-inspection of #{i.parent_inspection_id}</span>}
                {i.inspector_name && <span style={meta}> · {i.inspector_name}</span>}
              </button>
              <button style={outline} onClick={() => onArchive(i)}>Archive</button>
            </div>
            {openId === i.id && (
              <div style={detail}>
                <div style={dl}>
                  <span style={lbl}>Industrial user</span><span>{i.company_name}</span>
                  <span style={lbl}>Date</span><span>{i.inspection_date}</span>
                  <span style={lbl}>Type</span><span style={{ textTransform: "capitalize" }}>{i.inspection_type.replace("_", " ")}</span>
                  <span style={lbl}>Result</span><span>{i.result === "deficiencies" ? "Deficiencies" : "Satisfactory"}</span>
                  <span style={lbl}>Inspector</span><span>{i.inspector_name || "—"}</span>
                  <span style={lbl}>Findings</span>
                  <span style={{ whiteSpace: "pre-wrap" }}>{i.findings || "None recorded."}</span>
                  {i.follow_up_required && (<>
                    <span style={lbl}>Re-inspection</span>
                    <span>{i.follow_up_open ? "Open — log a re-inspection to close it"
                      : `Resolved ${i.resolved_at ? i.resolved_at.slice(0, 10) : ""}`}</span>
                  </>)}
                  {i.parent_inspection_id && (<>
                    <span style={lbl}>Re-inspection of</span><span>inspection #{i.parent_inspection_id}</span>
                  </>)}
                  <span style={lbl}>Logged by</span>
                  <span>{i.created_by_name || "—"}{i.created_at ? ` · ${i.created_at.slice(0, 10)}` : ""}</span>
                </div>
                <DocumentsPanel entityType="inspection" entityId={i.id} category="inspection_report"
                  title="Inspection report" emptyText="No report attached yet — upload the signed report or checklist (PDF)." />
              </div>
            )}
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
  <div>
    <div style={{ fontSize: 24, fontWeight: 700, color: color ?? "#2d3748" }}>{value}</div>
    <div style={{ fontSize: 12, color: "#718096" }}>{label}</div>
  </div>
);

const card: React.CSSProperties = { border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 14 };
const row: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center",
  gap: 8, fontSize: 13, padding: "7px 0", borderBottom: "1px solid #eee" };
const detail: React.CSSProperties = { background: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: 6,
  padding: "10px 12px", margin: "4px 0 10px", fontSize: 13 };
const dl: React.CSSProperties = { display: "grid", gridTemplateColumns: "140px minmax(0, 1fr)", gap: "4px 12px",
  margin: "0 0 10px" };
const rowBtn: React.CSSProperties = { minWidth: 0, flex: 1, background: "none", border: "none", padding: 0,
  textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit" };
const meta: React.CSSProperties = { color: "#718096" };
const field: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#4a5568" };
const inp: React.CSSProperties = { padding: "7px 9px", border: "1px solid #cbd5e0", borderRadius: 6, fontSize: 14 };
const sel: React.CSSProperties = { ...inp, padding: "6px 8px" };
const btn: React.CSSProperties = { background: "#3182ce", color: "#fff", border: "none", borderRadius: 6,
  padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const outline: React.CSSProperties = { background: "#fff", color: "#4a5568", border: "1px solid #cbd5e0",
  borderRadius: 6, padding: "3px 10px", fontSize: 12, cursor: "pointer" };

export default InspectionsPanel;
